import type { Dictionary } from '@ngrx/entity';
import { createSelector, type MemoizedSelector } from '@ngrx/store';
import type { CompletenessMode } from '../../../interfaces/DeliveryPointIndicator.interface';
import type { Project, ProjectId } from '../../../interfaces/Project.interface';
import { type DeliveryPointMetric, TimeseriesIndicatorCode } from '../../../interfaces/Timeseries.interface';
import { VatModes } from '../../../providers/budgets/budgets.constants';
import type { AppState } from '../../app.state';
import { selectEntitiesState } from '../entities-state.selector';
import { projectAdapter, type State } from './projects.state';

const getIsPending = (state: State): boolean => state.isPending;
const getIsChanging = (state: State): boolean => state.isChanging;
const getIsLoaded = (state: State): boolean => state.isLoaded;
const getCurrentProjectId = (state: State): string | null => state.currentProjectId;

const getProjectVatMode = (project: Project | null): VatModes | null => {
    if (!project) return null;
    return project.isVatIncl ? VatModes.VAT_INCL : VatModes.VAT_EXCL;
};

const getProjectCompletenessSources = (project: Project | null): DeliveryPointMetric[] => {
    if (!project) return [];
    return project.completenessSources;
};

const getProjectCompletenessMode = (project: Project | null): CompletenessMode | null => {
    if (!project) return null;
    return project.completenessMode;
};

const getVatModeTimeseriesIndicatorCode = (vatMode: VatModes | null): TimeseriesIndicatorCode | null => {
    if (!vatMode) return null;
    return vatMode === VatModes.VAT_EXCL
        ? TimeseriesIndicatorCode.COST_VAT_EXCL
        : TimeseriesIndicatorCode.COST_VAT_INCL;
};

export const selectProjectsState: MemoizedSelector<AppState, State> = createSelector(
    selectEntitiesState,
    entitiesState => entitiesState.projects
);

export const selectProjects: (state: AppState) => Project[] =
    projectAdapter.getSelectors(selectProjectsState).selectAll;

export const selectProjectsMap: (state: AppState) => Dictionary<Project> =
    projectAdapter.getSelectors(selectProjectsState).selectEntities;

export const selectProjectById: (projectId: ProjectId) => MemoizedSelector<AppState, Project | undefined> = (
    projectId: ProjectId
) => createSelector(selectProjectsMap, projectsMap => projectsMap[projectId]);

export const selectIsPending: MemoizedSelector<AppState, boolean> = createSelector(selectProjectsState, getIsPending);

export const selectIsChanging: MemoizedSelector<AppState, boolean> = createSelector(selectProjectsState, getIsChanging);

export const selectIsLoaded: MemoizedSelector<AppState, boolean> = createSelector(selectProjectsState, getIsLoaded);

export const selectCurrentProjectId: MemoizedSelector<AppState, ProjectId | null> = createSelector(
    selectProjectsState,
    getCurrentProjectId
);

export const selectCurrentProject: MemoizedSelector<AppState, Project | null> = createSelector(
    selectProjectsMap,
    selectCurrentProjectId,
    (projectsMap, currentProjectId) => (currentProjectId ? (projectsMap[currentProjectId] ?? null) : null)
);

export const selectCurrentProjectVatMode: MemoizedSelector<AppState, VatModes | null> = createSelector(
    selectCurrentProject,
    getProjectVatMode
);

export const selectCurrentProjectVatModeTimeseriesIndicatorCode: MemoizedSelector<
    AppState,
    TimeseriesIndicatorCode | null
> = createSelector(selectCurrentProjectVatMode, getVatModeTimeseriesIndicatorCode);

export const selectCurrentProjectCompletenessSources: MemoizedSelector<AppState, DeliveryPointMetric[]> =
    createSelector(selectCurrentProject, getProjectCompletenessSources);

export const selectCurrentProjectCompletenessMode: MemoizedSelector<AppState, CompletenessMode | null> = createSelector(
    selectCurrentProject,
    getProjectCompletenessMode
);
